import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { T } from "@/components/T";

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.06, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach(el => observer.observe(el));
    return () => els.forEach(el => observer.unobserve(el));
  });
}

const vorteileDE = [
  "Spannende Projekte – von Meran bis in die ganze Region",
  "Teil eines gewachsenen Unternehmens (Hafner EC GmbH)",
  "Klare Strukturen, verlässliche Abläufe",
  "Weiterbildung und fachliche Entwicklung",
  "Gute Arbeitszeiten und faire Vergütung",
  "Team, das zusammenhält"
];

const vorteileIT = [
  "Progetti stimolanti – da Merano a tutta la regione",
  "Parte di un'azienda consolidata (Hafner EC GmbH)",
  "Strutture chiare, processi affidabili",
  "Formazione e sviluppo professionale",
  "Buoni orari di lavoro e retribuzione equa",
  "Un team coeso"
];

const stellen = [
  {
    titel_de: "Zimmerer / Zimmermann (m/w/d)",
    titel_it: "Falegname / Carpentiere (m/f/d)",
    typ_de: "Vollzeit",
    typ_it: "Tempo pieno",
    text_de: "Sie kennen Holz. Sie arbeiten präzise. Sie wollen mehr als Routine. Dann passen Sie zu uns.",
    text_it: "Conoscete il legno. Lavorate con precisione. Volete più della routine. Allora siete adatti a noi."
  },
  {
    titel_de: "Holzbau-Monteur (m/w/d)",
    titel_it: "Montatore costruzione in legno (m/f/d)",
    typ_de: "Vollzeit",
    typ_it: "Tempo pieno",
    text_de: "Montage auf der Baustelle, Teamarbeit und selbstständiges Arbeiten – für erfahrene Fachkräfte.",
    text_it: "Montaggio in cantiere, lavoro di squadra e autonomo – per professionisti esperti."
  },
  {
    titel_de: "Vorarbeiter Holzbau (m/w/d)",
    titel_it: "Caposquadra costruzione in legno (m/f/d)",
    typ_de: "Vollzeit",
    typ_it: "Tempo pieno",
    text_de: "Koordination des Teams auf der Baustelle. Qualitätsverantwortung. Ansprechpartner vor Ort.",
    text_it: "Coordinamento del team in cantiere. Responsabilità della qualità. Referente sul posto."
  }
];

export default function Karriere() {
  const { lang } = useLanguage();
  useReveal();

  const vorteile = lang === 'it' ? vorteileIT : vorteileDE;

  return (
    <div className="pt-20 md:pt-24">
      {/* Header */}
      <section className="section-gap bg-hb-off-white border-b border-hb-border">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-6 reveal">
            <T de="Karriere" it="Carriera" />
          </span>
          <h1 className="section-title max-w-2xl mb-6 reveal reveal-delay-1" data-testid="karriere-heading">
            <T de="Bauen mit uns." it="Costruire con noi." />
          </h1>
          <p className="section-subtitle max-w-xl reveal reveal-delay-2">
            <T de="Wir suchen Menschen, die Holzbau ernst nehmen. Die präzise arbeiten wollen. Die stolz auf ihre Arbeit sein möchten."
               it="Cerchiamo persone che prendano sul serio la costruzione in legno. Che vogliano lavorare con precisione. Che vogliano essere orgogliose del proprio lavoro." />
          </p>
        </div>
      </section>

      {/* Warum Holzbuddy */}
      <section className="section-gap bg-white" data-testid="karriere-content">
        <div className="container-hb">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <span className="label-text text-hb-wood block mb-5 reveal">
                <T de="Warum wir?" it="Perché noi?" />
              </span>
              <h2 className="text-3xl sm:text-4xl font-manrope font-extrabold text-hb-dark mb-6 reveal reveal-delay-1">
                <T de="Mehr als ein Job." it="Più di un lavoro." />
              </h2>
              <div className="space-y-4 reveal reveal-delay-2">
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Bei Holzbuddy werden Sie nicht eine Nummer unter vielen. Wir sind ein überschaubares Team mit klarer Ausrichtung – und Teil der Hafner EC GmbH mit all ihren Ressourcen und Möglichkeiten."
                     it="Da Holzbuddy non sarete un numero tra tanti. Siamo un team contenuto con orientamento chiaro – e parte della Hafner EC GmbH con tutte le sue risorse e possibilità." />
                </p>
                <p className="text-base text-hb-text font-inter leading-relaxed">
                  <T de="Wir glauben daran, dass gute Arbeit gute Rahmenbedingungen braucht. Daher investieren wir in Ausrüstung, Planung und Menschen."
                     it="Crediamo che un buon lavoro necessiti di buone condizioni. Per questo investiamo in attrezzature, pianificazione e persone." />
                </p>
              </div>
            </div>
            <div className="bg-hb-off-white p-8">
              <h3 className="text-xs tracking-widest uppercase text-hb-muted font-semibold mb-5">
                <T de="Was Sie erwartet" it="Cosa vi aspetta" />
              </h3>
              <ul className="space-y-4">
                {vorteile.map((v, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-hb-text font-inter">
                    <Check size={14} className="text-hb-wood mt-0.5 flex-shrink-0" />
                    {v}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team image */}
      <section className="bg-white">
        <div className="container-hb">
          <div className="grid grid-cols-3 gap-px bg-hb-border">
            {[
              "https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-44-DSCF0700-1229x1536.jpg",
              "https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-63-DSCF0752-819x1024.jpg",
              "https://new.holzbuddy.team/wp-content/uploads/2025/03/Holzbuddy-Team-Hafner-EC-Baufirma-Zimmerer-32-DSCF0668-768x1024.jpg"
            ].map((img, i) => (
              <div key={i} className="aspect-[3/4] overflow-hidden">
                <img loading="lazy" src={img} alt="Team Holzbuddy" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Offene Stellen */}
      <section className="section-gap bg-hb-off-white" data-testid="stellen-section">
        <div className="container-hb">
          <span className="label-text text-hb-wood block mb-5 reveal">
            <T de="Offene Stellen" it="Posizioni aperte" />
          </span>
          <h2 className="section-title mb-14 reveal reveal-delay-1">
            <T de="Aktuelle Positionen." it="Posizioni attuali." />
          </h2>
          <div className="space-y-px bg-hb-border">
            {stellen.map((s, i) => (
              <div
                key={i}
                data-testid={`stelle-${i}`}
                className={`bg-white p-8 flex flex-col md:flex-row justify-between gap-6 reveal reveal-delay-${i + 1}`}
              >
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-manrope font-bold text-hb-dark">
                      {lang === 'it' ? s.titel_it : s.titel_de}
                    </h3>
                    <span className="text-[10px] tracking-widest uppercase text-hb-wood font-semibold border border-hb-wood/30 px-2 py-0.5">
                      {lang === 'it' ? s.typ_it : s.typ_de}
                    </span>
                  </div>
                  <p className="text-sm text-hb-text font-inter">
                    {lang === 'it' ? s.text_it : s.text_de}
                  </p>
                </div>
                <a
                  href="mailto:info@holzbuddy.team?subject=Bewerbung"
                  data-testid={`bewerben-${i}`}
                  className="btn-secondary flex-shrink-0 self-start md:self-center"
                >
                  <T de="Bewerben" it="Candidarsi" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Initiativbewerbung */}
      <section className="py-20 bg-hb-dark" data-testid="initiativbewerbung">
        <div className="container-hb text-center">
          <h2 className="text-3xl font-manrope font-extrabold text-white mb-4">
            <T de="Passende Stelle nicht dabei?" it="La posizione giusta non c'è?" />
          </h2>
          <p className="text-white/60 font-inter mb-8 max-w-md mx-auto">
            <T de="Wir freuen uns auch über Initiativbewerbungen. Schreiben Sie uns – wir melden uns."
               it="Accettiamo volentieri anche candidature spontanee. Scriveteci – vi risponderemo." />
          </p>
          <a
            href="mailto:info@holzbuddy.team?subject=Initiativbewerbung Holzbuddy"
            data-testid="initiativ-btn"
            className="btn-primary bg-white text-hb-dark hover:bg-hb-off-white"
          >
            <T de="Initiativbewerbung senden" it="Invia candidatura spontanea" />
          </a>
        </div>
      </section>
    </div>
  );
}
